import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { assignProvider, getEligibleCrew } from "../../services/workflowService";

/**
 * Quick "assign the service provider" for a booking taken without one (its workflow's service step is
 * Optional). Lists the vendor's professionals, with who is already busy at that time; picking someone
 * who is busy is allowed - it is only a hint, as everywhere else. Saving tells the crew they were assigned.
 *   booking   a dashboard row ({ group_id, service_date, service_time, display_booking_id })
 *   onSaved   () => void   called after a successful save (the list reloads)
 */
export default function AssignProviderModal({ show, booking, crewLabel = "Service Provider", onHide, onSaved }) {
  const [crew, setCrew] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const groupId = booking?.group_id;
  const date = String(booking?.service_date || "").slice(0, 10);
  const time = String(booking?.service_time || "").slice(0, 5);

  useEffect(() => {
    if (!show || !groupId) return undefined;
    let cancelled = false;
    setPicked([]);
    setError("");
    setLoading(true);
    (async () => {
      try {
        const res = await getEligibleCrew({ skill: "PROFESSIONAL", date, time, excludeGroupId: groupId });
        if (cancelled) return;
        if (res?.success) setCrew(res.data?.crew || []);
        else setError(res?.message || "Could not load the list");
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not load the list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, groupId, date, time]);

  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!picked.length) return;
    setSaving(true);
    try {
      const res = await assignProvider({ groupId, userIds: picked });
      if (res?.success === false) {
        toast.error(res.message || "Could not assign");
        return;
      }
      toast.success(`${crewLabel} assigned`);
      onSaved?.();
      onHide?.();
    } catch (e) {
      toast.error(e?.message || "Could not assign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Assign {crewLabel}
          {booking?.display_booking_id ? <span className="text-muted"> · {booking.display_booking_id}</span> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        )}
        {!loading && error && <div className="alert alert-warning py-2">{error}</div>}
        {!loading && !error && crew.length === 0 && <div className="text-muted">No one is available to assign.</div>}
        {!loading &&
          crew.map((c) => (
            <Form.Check
              key={c.user_id}
              type="checkbox"
              id={`assign-provider-${c.user_id}`}
              className="mb-2"
              checked={picked.includes(c.user_id)}
              onChange={() => toggle(c.user_id)}
              label={
                <span>
                  {c.name}
                  {c.busy ? <span style={{ color: "#B45309" }}> · busy{c.busy_with ? ` with ${c.busy_with}` : ""} at this time</span> : null}
                </span>
              }
            />
          ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="danger" onClick={save} disabled={!picked.length || saving}>
          {saving ? "Saving…" : "Assign"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
